import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './SimpleDateRangePicker.css';
import moment from 'moment';

const SimpleDateRangePicker = ({
  startDate,
  endDate,
  onChange,
  placeholder = "Select Date Range",
  className = "",
  monthsShown = 2,
  maxDate = new Date()
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localStartDate, setLocalStartDate] = useState(startDate);
  const [localEndDate, setLocalEndDate] = useState(endDate);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  const updatePosition = useCallback(() => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: Math.max(rect.width, 320),
      });
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
    }
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        inputRef.current && !inputRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleQuickSelect = (range) => {
    let start, end;
    const now = moment();

    switch (range) {
      case 'today':
        start = now.startOf('day').toDate();
        end = now.endOf('day').toDate();
        break;
      case 'yesterday':
        start = now.subtract(1, 'days').startOf('day').toDate();
        end = moment().subtract(1, 'days').endOf('day').toDate();
        break;
      case 'thisWeek':
        start = moment().startOf('week').toDate();
        end = moment().endOf('week').toDate();
        break;
      case 'lastWeek':
        start = moment().subtract(1, 'weeks').startOf('week').toDate();
        end = moment().subtract(1, 'weeks').endOf('week').toDate();
        break;
      case 'thisMonth':
        start = moment().startOf('month').toDate();
        end = moment().endOf('month').toDate();
        break;
      case 'lastMonth':
        start = moment().subtract(1, 'months').startOf('month').toDate();
        end = moment().subtract(1, 'months').endOf('month').toDate();
        break;
      case 'last7Days':
        start = moment().subtract(6, 'days').startOf('day').toDate();
        end = moment().endOf('day').toDate();
        break;
      case 'last30Days':
        start = moment().subtract(29, 'days').startOf('day').toDate();
        end = moment().endOf('day').toDate();
        break;
      default:
        return;
    }

    setLocalStartDate(start);
    setLocalEndDate(end);
    onChange([start, end]);
    setIsOpen(false);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setLocalStartDate(start);
    setLocalEndDate(end);
    
    if (start && end) {
      onChange([start, end]);
      setIsOpen(false);
    }
  };

  const getDisplayText = () => {
    if (localStartDate && localEndDate) {
      return `${moment(localStartDate).format('MMM DD, YYYY')} - ${moment(localEndDate).format('MMM DD, YYYY')}`;
    }
    return placeholder;
  };

  const getActiveRange = () => {
    if (!localStartDate || !localEndDate) return null;

    const start = moment(localStartDate).startOf('day');
    const end = moment(localEndDate).endOf('day');
    const now = moment();

    if (start.isSame(now.clone().startOf('day'), 'day') && 
        end.isSame(now.clone().endOf('day'), 'day')) {
      return 'today';
    }

    if (start.isSame(now.clone().subtract(1, 'days').startOf('day'), 'day') && 
        end.isSame(now.clone().subtract(1, 'days').endOf('day'), 'day')) {
      return 'yesterday';
    }

    if (start.isSame(moment().startOf('week'), 'day') && 
        end.isSame(moment().endOf('week'), 'day')) {
      return 'thisWeek';
    }

    if (start.isSame(moment().subtract(1, 'weeks').startOf('week'), 'day') && 
        end.isSame(moment().subtract(1, 'weeks').endOf('week'), 'day')) {
      return 'lastWeek';
    }

    if (start.isSame(moment().startOf('month'), 'day') && 
        end.isSame(moment().endOf('month'), 'day')) {
      return 'thisMonth';
    }

    if (start.isSame(moment().subtract(1, 'months').startOf('month'), 'day') && 
        end.isSame(moment().subtract(1, 'months').endOf('month'), 'day')) {
      return 'lastMonth';
    }

    if (start.isSame(moment().subtract(6, 'days').startOf('day'), 'day') && 
        end.isSame(moment().endOf('day'), 'day')) {
      return 'last7Days';
    }

    if (start.isSame(moment().subtract(29, 'days').startOf('day'), 'day') && 
        end.isSame(moment().endOf('day'), 'day')) {
      return 'last30Days';
    }

    return null;
  };

  const activeRange = getActiveRange();

  const dropdownContent = isOpen ? (
    <div
      ref={dropdownRef}
      className="date-range-dropdown"
      style={{
        position: 'absolute',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        zIndex: 99999,
      }}
    >
      <div className="date-range-content">
        <div className="quick-select-section">
          <div className="quick-select-header">
            <i className="fa fa-clock me-2"></i>
            Quick Select
          </div>
          
          <div className="quick-select-grid">
            <button
              className={`quick-select-btn ${activeRange === 'today' ? 'active' : ''}`}
              onClick={() => handleQuickSelect('today')}
            >
              Today
            </button>
            <button
              className={`quick-select-btn ${activeRange === 'yesterday' ? 'active' : ''}`}
              onClick={() => handleQuickSelect('yesterday')}
            >
              Yesterday
            </button>
            <button
              className={`quick-select-btn ${activeRange === 'thisWeek' ? 'active' : ''}`}
              onClick={() => handleQuickSelect('thisWeek')}
            >
              This Week
            </button>
            <button
              className={`quick-select-btn ${activeRange === 'lastWeek' ? 'active' : ''}`}
              onClick={() => handleQuickSelect('lastWeek')}
            >
              Last Week
            </button>
            <button
              className={`quick-select-btn ${activeRange === 'thisMonth' ? 'active' : ''}`}
              onClick={() => handleQuickSelect('thisMonth')}
            >
              This Month
            </button>
            <button
              className={`quick-select-btn ${activeRange === 'lastMonth' ? 'active' : ''}`}
              onClick={() => handleQuickSelect('lastMonth')}
            >
              Last Month
            </button>
            <button
              className={`quick-select-btn ${activeRange === 'last7Days' ? 'active' : ''}`}
              onClick={() => handleQuickSelect('last7Days')}
            >
              Last 7 Days
            </button>
            <button
              className={`quick-select-btn ${activeRange === 'last30Days' ? 'active' : ''}`}
              onClick={() => handleQuickSelect('last30Days')}
            >
              Last 30 Days
            </button>
          </div>
        </div>

        <div className="date-picker-section">
          <DatePicker
            selected={localStartDate}
            onChange={handleDateChange}
            startDate={localStartDate}
            endDate={localEndDate}
            selectsRange
            inline
            monthsShown={monthsShown}
            maxDate={maxDate}
          />
        </div>
      </div>
    </div>
  ) : null;

  return (
    <div className={`simple-date-range-picker ${className}`} ref={inputRef}>
      <div 
        className="date-range-input"
        onClick={() => setIsOpen(!isOpen)}
      >
        <i className="fa fa-calendar me-2"></i>
        <span>{getDisplayText()}</span>
        <i className={`fa fa-chevron-${isOpen ? 'up' : 'down'} ms-auto`}></i>
      </div>

      {ReactDOM.createPortal(dropdownContent, document.body)}
    </div>
  );
};

export default SimpleDateRangePicker;
